import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useParams, Link, useNavigate, Navigate } from 'react-router-dom';
import Moment from 'react-moment';

import {
  fetchPlayerOverview,
  fetchPlayerParameters,
  fetchPlayerMatchStats,
  fetchMarkersBulk,
  fetchMatchDetail,
  createVideoList,
} from './api';
import { useMappedPlayerId } from './hooks';
import {
  collectMarkerIds,
  buildMarkerParamIndex,
  buildEventsFromMarkers,
  formatSeconds,
  BODY_PARTS,
  bodyPartLexId,
} from './matchEvents';
import { useLexicon } from '../../hooks/useLexicon';
import { getMediaUrl } from '../../helpers';
import {
  buildParamGroupsForPlayer,
  isGoalkeeper,
} from '../../helpers/parameterGroups';
import play from '../../images/ic_play-button.svg';
import PlayerLoader, { InlinePlayerLoader } from '../../components/PlayerLoader';
import MatchReportButton from '../../components/MatchReportButton';

const findMatch = (matches, matchId) =>
  (matches || []).find((m) => String(m.id) === String(matchId));

// Normalize the /platform/match/{id} response to the same shape findMatch
// returns from matches_list. Some matches (older seasons, restricted comps)
// don't show up in the player overview's matches_list but still load via
// the platform detail endpoint, so we fall back to that to render the
// header card. Logos in detail.home_squad.team.logo are already absolute
// api-v0 URLs, so getMediaUrl can pass them through unchanged.
const normalizeDetailToMatch = (detail) => {
  if (!detail || typeof detail !== 'object') return null;
  const homeTeam = detail.home_squad?.team || {};
  const awayTeam = detail.away_squad?.team || {};
  return {
    id: detail.id,
    date: detail.date,
    score: detail.score,
    home_team_score: detail.home_team_score,
    away_team_score: detail.away_team_score,
    home_team: homeTeam.name ?? null,
    away_team: awayTeam.name ?? null,
    home_team_logo: homeTeam.logo ?? null,
    away_team_logo: awayTeam.logo ?? null,
    competition: detail.season?.competition?.name ?? detail.season?.title ?? null,
    competition_logo: detail.season?.competition_logo
      ?? detail.season?.competition?.logo
      ?? null,
    video: (detail.videos || []).find((v) => v?.link)?.link ?? null,
    videos: detail.videos || [],
  };
};

const findMatchRow = (matchStats, matchId) => {
  const all = Array.isArray(matchStats)
    ? matchStats
    : Array.isArray(matchStats?.results)
      ? matchStats.results
      : [];
  return all.find((m) => String(m?.id) === String(matchId)) || null;
};

const getRowParameters = (row) => {
  if (!row) return null;
  return Array.isArray(row.parameters)
    ? row.parameters
    : Array.isArray(row.params)
      ? row.params
      : [];
};

const PlayerMatchDetail = () => {
  const l = useLexicon();
  const { id: matchId } = useParams();
  const mappedPlayerId = useMappedPlayerId();
  const navigate = useNavigate();

  const [overview, setOverview] = useState(null);
  const [parameters, setParameters] = useState(null);
  const [matchStats, setMatchStats] = useState(null);
  const [matchDetail, setMatchDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [selectedEvents, setSelectedEvents] = useState(() => new Set());
  const [selectedCategory, setSelectedCategory] = useState(null); // null = "All"
  const [creatingShareLink, setCreatingShareLink] = useState(false);
  const [selectedParamIds, setSelectedParamIds] = useState(() => new Set());
  const [selectedBodyPart, setSelectedBodyPart] = useState(null);
  const [bodyPartOpen, setBodyPartOpen] = useState(false);
  const [hideZeroStats, setHideZeroStats] = useState(true);
  // Which stat card is currently minting a video token (per-card spinner).
  const [busyStatId, setBusyStatId] = useState(null);

  useEffect(() => {
    if (!mappedPlayerId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      fetchPlayerOverview(mappedPlayerId),
      fetchPlayerParameters(),
      fetchMatchDetail(matchId).catch(() => null),
    ])
      .then(async ([overviewRes, paramsRes, detailRes]) => {
        if (cancelled) return;
        const overviewData = overviewRes.data;
        const paramsData = paramsRes.data?.results ?? paramsRes.data ?? [];
        const detail = detailRes?.data ?? null;
        setOverview(overviewData);
        setParameters(paramsData);
        setMatchDetail(detail);

        const resolvedSeasonId = detail?.season?.year_season?.id
          ?? overviewData?.selected_season?.id
          ?? overviewData?.available_seasons?.[0]?.id;
        const paramIds = (Array.isArray(paramsData) ? paramsData : []).map((p) => p.id);

        if (resolvedSeasonId && paramIds.length > 0) {
          const statsRes = await fetchPlayerMatchStats(mappedPlayerId, resolvedSeasonId, paramIds);
          if (cancelled) return;
          setMatchStats(statsRes.data);
        } else {
          setMatchStats([]);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [mappedPlayerId, matchId]);

  const match = useMemo(
    () =>
      findMatch(overview?.matches_list, matchId)
      ?? normalizeDetailToMatch(matchDetail),
    [overview?.matches_list, matchId, matchDetail]
  );

  const player = overview?.player ?? overview;
  const matchRow = useMemo(
    () => findMatchRow(matchStats, matchId),
    [matchStats, matchId]
  );
  const matchParameters = useMemo(() => getRowParameters(matchRow), [matchRow]);

  useEffect(() => {
    if (!matchRow || !mappedPlayerId) {
      setEvents([]);
      return undefined;
    }
    const markerIds = collectMarkerIds(matchRow);
    if (markerIds.length === 0) {
      setEvents([]);
      return undefined;
    }

    let cancelled = false;
    setEventsLoading(true);

    const opts = selectedBodyPart != null ? { bodyPartId: [selectedBodyPart] } : {};
    fetchMarkersBulk(markerIds, opts)
      .then((res) => {
        if (cancelled) return;
        const markers = res.data?.events ?? [];
        setEvents(buildEventsFromMarkers(markers));
      })
      .catch(() => {
        if (!cancelled) setEvents([]);
      })
      .finally(() => {
        if (!cancelled) setEventsLoading(false);
      });

    return () => { cancelled = true; };
  }, [matchRow, mappedPlayerId, matchId, selectedBodyPart]);

  // Switching categories clears the param-level selection so checkboxes
  // from the previous category don't leak into the new one's filter.
  useEffect(() => {
    setSelectedParamIds(new Set());
  }, [selectedCategory]);

  const groupedStats = useMemo(() => {
    if (!matchParameters || !parameters) return [];
    const byId = new Map((matchParameters || []).map((p) => [
      Number(p.parameter_id ?? p.id),
      p,
    ]));
    const enrichedParameters = (parameters || []).map((meta) => {
      const s = byId.get(Number(meta.id));
      return s ? { ...meta, count: s.count ?? s.value ?? 0 } : null;
    }).filter(Boolean);

    const groups = buildParamGroupsForPlayer(enrichedParameters, player);
    return groups;
  }, [matchParameters, parameters, player]);

  // Apply the "hide empty" toggle: drop stat items with count == 0, then
  // drop any group that ends up with no items. The headline strip is
  // unaffected — those three are a deliberate snapshot, even at zero.
  const visibleGroupedStats = useMemo(() => {
    if (!hideZeroStats) return groupedStats;
    return groupedStats
      .map(([groupName, items]) => [
        groupName,
        items.filter((item) => Number(item.count) > 0),
      ])
      .filter(([, items]) => items.length > 0);
  }, [groupedStats, hideZeroStats]);

  // Index of marker.id -> Set<paramId>, used to filter events by category.
  const markerParamIndex = useMemo(
    () => buildMarkerParamIndex(matchRow),
    [matchRow]
  );

  // For each group in groupedStats, the set of param IDs in that group.
  const groupParamIds = useMemo(() => {
    const map = new Map();
    groupedStats.forEach(([groupName, items]) => {
      map.set(groupName, new Set(items.map((it) => Number(it.id))));
    });
    return map;
  }, [groupedStats]);

  // Categories that have at least one event in this match — keeps the chip
  // strip tight by hiding empty categories (e.g. "Set pieces" for an
  // attacking midfielder who didn't take any).
  const availableCategories = useMemo(() => {
    if (events.length === 0) return [];
    const groupsWithEvents = [];
    groupParamIds.forEach((paramSet, groupName) => {
      const hasEvent = events.some((e) => {
        const markerParams = markerParamIndex.get(e.markerId);
        if (!markerParams) return false;
        for (const pid of markerParams) {
          if (paramSet.has(pid)) return true;
        }
        return false;
      });
      if (hasEvent) groupsWithEvents.push(groupName);
    });
    return groupsWithEvents;
  }, [events, groupParamIds, markerParamIndex]);

  // Reset to "All" if the user's chosen category disappears (e.g. after
  // matchRow reload).
  useEffect(() => {
    if (selectedCategory && !availableCategories.includes(selectedCategory)) {
      setSelectedCategory(null);
    }
  }, [selectedCategory, availableCategories]);

  // groupName -> items[] for the second-level param picker.
  const groupItems = useMemo(() => {
    const map = new Map();
    groupedStats.forEach(([groupName, items]) => map.set(groupName, items));
    return map;
  }, [groupedStats]);

  // Per-param event count across the current selection. One pass over events.
  const paramEventCounts = useMemo(() => {
    const counts = new Map();
    events.forEach((e) => {
      const mp = markerParamIndex.get(e.markerId);
      if (!mp) return;
      mp.forEach((pid) => counts.set(pid, (counts.get(pid) ?? 0) + 1));
    });
    return counts;
  }, [events, markerParamIndex]);

  // Items in the active category that have ≥1 event in scope.
  const availableParamsForCategory = useMemo(() => {
    if (!selectedCategory) return [];
    const items = groupItems.get(selectedCategory) ?? [];
    return items
      .map((p) => {
        const id = Number(p.id);
        return { id, name: p.name, count: paramEventCounts.get(id) ?? 0 };
      })
      .filter((p) => p.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [selectedCategory, groupItems, paramEventCounts]);

  // Two-level filter: specific params win over category. If params are
  // checked we narrow to those; otherwise we fall back to the category's
  // full param set; otherwise show everything.
  const filteredEvents = useMemo(() => {
    if (selectedParamIds.size > 0) {
      return events.filter((e) => {
        const mp = markerParamIndex.get(e.markerId);
        if (!mp) return false;
        for (const pid of mp) if (selectedParamIds.has(pid)) return true;
        return false;
      });
    }
    if (selectedCategory === null) return events;
    const paramSet = groupParamIds.get(selectedCategory);
    if (!paramSet) return events;
    return events.filter((e) => {
      const markerParams = markerParamIndex.get(e.markerId);
      if (!markerParams) return false;
      for (const pid of markerParams) {
        if (paramSet.has(pid)) return true;
      }
      return false;
    });
  }, [events, selectedCategory, selectedParamIds, groupParamIds, markerParamIndex]);

  if (!mappedPlayerId) {
    return <Navigate to="/player/not-mapped" replace />;
  }

  const statsLoaded = !loading && matchStats !== null;
  const hasStats = statsLoaded && groupedStats.length > 0;
  const didNotPlay = statsLoaded && match && !hasStats;
  const hasVideo = Boolean(match?.video || match?.videos?.some((v) => v?.link));

  // Season + competition resolution for the per-match PDF download. Prefer
  // the platform/match detail (has the canonical year_season), fall back to
  // the player overview's selected season.
  const resolvedSeasonId = matchDetail?.season?.year_season?.id
    ?? overview?.selected_season?.id
    ?? overview?.available_seasons?.[0]?.id
    ?? null;
  const resolvedCompetitionId = match?.competition_id
    ?? matchDetail?.season?.competition?.id
    ?? null;

  const toggleEvent = (key) => {
    setSelectedEvents((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const selectAllEvents = () => {
    setSelectedEvents(new Set(filteredEvents.map((e) => e.key)));
  };

  const clearSelection = () => setSelectedEvents(new Set());

  const toggleParam = (paramId) => {
    setSelectedParamIds((prev) => {
      const next = new Set(prev);
      const id = Number(paramId);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const clearParams = () => setSelectedParamIds(new Set());

  // The video player is the token-based EventsVideo flow used by /player/events.
  // Build a video-list token from a set of marker IDs, then navigate. Same UX
  // for "Watch my events" header CTA, single-event tap, and "Watch selected".
  const openInVideoPlayer = async (markerIds) => {
    if (creatingShareLink) return;
    const ids = Array.from(markerIds).map((k) => Number(k)).filter(Number.isFinite);
    if (ids.length === 0) return;
    setCreatingShareLink(true);
    try {
      const fullName = player?.display_name
        || [player?.name, player?.surname].filter(Boolean).join(' ')
        || l(1800);
      const res = await createVideoList(ids, `Events - ${fullName}`);
      const token = res.data?.token;
      if (token) navigate(`/player/events/video?token=${token}`);
    } catch {
      // Surface as inline error if needed; keep the button usable.
    } finally {
      setCreatingShareLink(false);
    }
  };

  const openSelected = () => openInVideoPlayer(selectedEvents);
  const openSingleEvent = (eventKey) => openInVideoPlayer([eventKey]);
  const openAllEvents = () => openInVideoPlayer(events.map((e) => e.key));

  // Tapping a stat card opens the video player filtered to that parameter's
  // events in this match — the same "every number leads to its video" rule the
  // Home dashboard tiles follow (SMRTSTATS-361). Only cards with ≥1 watchable
  // event are wired up (see paramEventCounts).
  const openStatEvents = async (paramId) => {
    const pid = Number(paramId);
    const markerIds = events
      .filter((e) => markerParamIndex.get(e.markerId)?.has(pid))
      .map((e) => e.markerId);
    if (markerIds.length === 0) return;
    setBusyStatId(pid);
    try {
      await openInVideoPlayer(markerIds);
    } finally {
      setBusyStatId(null);
    }
  };

  return (
    <Wrap>
      <BackLink to="/player/matches">← Matches</BackLink>

      {loading && <PlayerLoader label={l(1825)} />}
      {error && <ErrorBox>{l(1826)}</ErrorBox>}

      {match && (
        <Header>
          <CompetitionRow>
            {match.competition_logo && (
              <CompLogo src={getMediaUrl(match.competition_logo)} alt="" />
            )}
            <CompetitionLabel>{match.competition}</CompetitionLabel>
          </CompetitionRow>
          <Teams>
            <TeamSide>
              {match.home_team_logo && (
                <TeamLogo src={getMediaUrl(match.home_team_logo)} alt="" />
              )}
              <TeamName>{match.home_team}</TeamName>
            </TeamSide>
            <ScoreBlock>
              <MatchDate>
                <Moment format="DD.MM.YYYY">{match.date}</Moment>
              </MatchDate>
              <Score>{match.score || `${match.home_team_score}:${match.away_team_score}`}</Score>
            </ScoreBlock>
            <TeamSide>
              {match.away_team_logo && (
                <TeamLogo src={getMediaUrl(match.away_team_logo)} alt="" />
              )}
              <TeamName>{match.away_team}</TeamName>
            </TeamSide>
          </Teams>
          {!didNotPlay && events.length > 0 && hasVideo && (
            <VideoCTA
              type="button"
              onClick={openAllEvents}
              disabled={creatingShareLink}
            >
              {creatingShareLink ? (
                <InlinePlayerLoader />
              ) : (
                <PlayIcon src={play} alt="" />
              )}
              {creatingShareLink ? l(1827) : l(1828)}
            </VideoCTA>
          )}
        </Header>
      )}

      {didNotPlay && (
        <DnpBox>
          <DnpBadge>DNP</DnpBadge>
          <DnpText>{l(1829)}</DnpText>
        </DnpBox>
      )}

      {hasStats && (
        <MatchReportButton
          seasonId={resolvedSeasonId}
          matchId={matchId}
          matchDate={match?.date}
          competitionId={resolvedCompetitionId}
          eligible={!didNotPlay}
        />
      )}

      {hasStats && (
        <StatsWrap>
          <HeadlineMetric player={player} matchParameters={matchParameters} parameters={parameters} />
          <ZeroToggle>
            <ZeroToggleInput
              type="checkbox"
              checked={hideZeroStats}
              onChange={(e) => setHideZeroStats(e.target.checked)}
            />
            <span>{l(1830)}</span>
          </ZeroToggle>
          {visibleGroupedStats.map(([groupName, items]) => (
            <StatGroup key={groupName}>
              <GroupTitle>{groupName}</GroupTitle>
              <StatsGrid>
                {items.map((item) => {
                  const pid = Number(item.id);
                  // A card is tappable only when this match actually has
                  // watchable events for that parameter.
                  const watchable = (paramEventCounts.get(pid) ?? 0) > 0;
                  const isBusy = busyStatId === pid;
                  return (
                    <StatCard
                      key={item.id}
                      as={watchable ? 'button' : 'div'}
                      type={watchable ? 'button' : undefined}
                      $clickable={watchable}
                      onClick={watchable ? () => openStatEvents(pid) : undefined}
                      disabled={watchable ? (creatingShareLink && !isBusy) : undefined}
                      aria-label={
                        watchable ? `${item.name}: ${item.count ?? 0} — ${l(1819)}` : undefined
                      }
                      aria-busy={isBusy || undefined}
                    >
                      <StatValue>
                        {isBusy ? <InlinePlayerLoader /> : (item.count ?? 0)}
                      </StatValue>
                      <StatLabel>{item.name}</StatLabel>
                      {watchable && !isBusy && <StatPlayHint src={play} alt="" />}
                    </StatCard>
                  );
                })}
              </StatsGrid>
            </StatGroup>
          ))}
        </StatsWrap>
      )}

      {!didNotPlay && eventsLoading && events.length === 0 && (
        <PlayerLoader label={l(1831)} />
      )}

      {!didNotPlay && events.length > 0 && (
        <EventsSection>
          <EventsHeader>
            <EventsTitle>{l(1832)} · {filteredEvents.length}</EventsTitle>
            <SelectLinks>
              {selectedEvents.size < filteredEvents.length && filteredEvents.length > 0 && (
                <SelectLink type="button" onClick={selectAllEvents}>{l(1833)}</SelectLink>
              )}
              {selectedEvents.size > 0 && (
                <SelectLink type="button" onClick={clearSelection}>{l(1023)}</SelectLink>
              )}
            </SelectLinks>
          </EventsHeader>

          {availableCategories.length > 0 && (
            <CategoryStrip>
              <CategoryChip
                type="button"
                $selected={selectedCategory === null}
                onClick={() => setSelectedCategory(null)}
              >
                {l(1837)} · {events.length}
              </CategoryChip>
              {availableCategories.map((groupName) => (
                <CategoryChip
                  key={groupName}
                  type="button"
                  $selected={selectedCategory === groupName}
                  onClick={() => setSelectedCategory(groupName)}
                >
                  {groupName}
                </CategoryChip>
              ))}
            </CategoryStrip>
          )}

          {selectedCategory && availableParamsForCategory.length > 0 && (
            <>
              <SubSectionHeader>
                <SectionLabel>{selectedCategory}</SectionLabel>
                <SectionActions>
                  {selectedParamIds.size > 0 && (
                    <ActionLink type="button" onClick={clearParams}>{l(1023)}</ActionLink>
                  )}
                </SectionActions>
              </SubSectionHeader>
              <ParamCard>
                <ParamScroll>
                  {availableParamsForCategory.map((p, idx) => {
                    const checked = selectedParamIds.has(p.id);
                    return (
                      <ParamRow key={p.id} $first={idx === 0}>
                        <ParamCheckbox
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleParam(p.id)}
                        />
                        <ParamMain>
                          <ParamName>{p.name}</ParamName>
                          <ParamCount>{p.count}</ParamCount>
                        </ParamMain>
                      </ParamRow>
                    );
                  })}
                </ParamScroll>
              </ParamCard>
            </>
          )}

          {/* Body-part filter — collapsed by default; tap to disclose chips. */}
          <Disclosure>
            <DisclosureToggle
              type="button"
              onClick={() => setBodyPartOpen((v) => !v)}
              aria-expanded={bodyPartOpen}
            >
              <DisclosureLabel>
                {l(1838)}
                {selectedBodyPart != null && (
                  <DisclosureValue>· {l(bodyPartLexId(selectedBodyPart))}</DisclosureValue>
                )}
              </DisclosureLabel>
              <DisclosureChevron $open={bodyPartOpen}>›</DisclosureChevron>
            </DisclosureToggle>

            {bodyPartOpen && (
              <BodyPartChips>
                {BODY_PARTS.map((bp) => {
                  const isSelected = bp.id === selectedBodyPart || (bp.id === null && selectedBodyPart === null);
                  return (
                    <CategoryChip
                      key={String(bp.id)}
                      type="button"
                      $selected={isSelected}
                      onClick={() => setSelectedBodyPart(bp.id)}
                    >
                      {l(bp.lid)}
                    </CategoryChip>
                  );
                })}
              </BodyPartChips>
            )}
          </Disclosure>

          {filteredEvents.length === 0 && (
            <Muted>{l(1834)}</Muted>
          )}

          <EventsList>
            {filteredEvents.map((e) => {
              const checked = selectedEvents.has(e.key);
              return (
                <EventRow key={e.key} $checked={checked}>
                  <EventCheckbox
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleEvent(e.key)}
                  />
                  <EventMain
                    type="button"
                    onClick={() => hasVideo && openSingleEvent(e.key)}
                    disabled={!hasVideo}
                  >
                    <EventName>{e.paramName}</EventName>
                    <EventTime>{formatSeconds(e.second)}</EventTime>
                  </EventMain>
                </EventRow>
              );
            })}
          </EventsList>
          {hasVideo && selectedEvents.size > 0 && (
            <WatchBar>
              <WatchButton
                type="button"
                onClick={openSelected}
                disabled={creatingShareLink}
              >
                {creatingShareLink ? (
                  <InlinePlayerLoader />
                ) : (
                  <PlayIcon src={play} alt="" />
                )}
                {creatingShareLink
                  ? l(1827)
                  : l(1835).replace('{count}', String(selectedEvents.size))}
              </WatchButton>
            </WatchBar>
          )}
          {!hasVideo && (
            <Muted>{l(1836)}</Muted>
          )}
        </EventsSection>
      )}
    </Wrap>
  );
};

const HEADLINE_FIELD_IDS = [2, 201, 206];
const HEADLINE_GK_IDS = [173, 1, 206];

const HeadlineMetric = ({ player, matchParameters, parameters }) => {
  const ids = isGoalkeeper(player) ? HEADLINE_GK_IDS : HEADLINE_FIELD_IDS;
  const byId = new Map((matchParameters || []).map((p) => [
    Number(p.parameter_id ?? p.id),
    p,
  ]));
  const labelById = new Map((parameters || []).map((p) => [Number(p.id), p.name]));
  const picks = ids
    .map((id) => {
      const stat = byId.get(id);
      if (!stat) return null;
      return { id, count: stat.count ?? stat.value ?? 0, label: labelById.get(id) ?? stat.name };
    })
    .filter(Boolean);
  if (picks.length === 0) return null;
  return (
    <HeadlineStrip>
      {picks.map((p) => (
        <HeadlineCell key={p.id}>
          <HeadlineValue>{p.count}</HeadlineValue>
          <HeadlineLabel>{p.label}</HeadlineLabel>
        </HeadlineCell>
      ))}
    </HeadlineStrip>
  );
};

export default PlayerMatchDetail;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  /* Bottom nav (~60px) + floating Watch pill (~50px at bottom: 96px) +
     breathing room — keeps the last event row reachable without the
     fixed CTA covering it when the user scrolls all the way down. */
  padding-bottom: calc(180px + env(safe-area-inset-bottom, 0px));
`;

const BackLink = styled(Link)`
  color: ${(props) => props.theme.blueish2};
  text-decoration: none;
  font-size: 14px;
`;

const Muted = styled.div`
  color: ${(props) => props.theme.blueish2};
  font-size: 14px;
`;

const ErrorBox = styled.div`
  color: ${(props) => props.theme.white};
  background: #3a1414;
  padding: 12px 16px;
  border-radius: 8px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: ${(props) => props.theme.row};
  border-radius: 12px;
`;

const CompetitionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CompLogo = styled.img`
  width: 18px;
  height: 18px;
  object-fit: contain;
`;

const CompetitionLabel = styled.span`
  font-size: 12px;
  color: ${(props) => props.theme.blueish2};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Teams = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const TeamSide = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
`;

const TeamLogo = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
`;

const TeamName = styled.span`
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 13px;
  text-align: center;
`;

const ScoreBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`;

const MatchDate = styled.div`
  font-size: 11px;
  color: ${(props) => props.theme.blueish2};
`;

const Score = styled.span`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 22px;
`;

const VideoCTA = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  background: ${(props) => props.theme.green};
  color: ${(props) => props.theme.black};
  border: 0;
  border-radius: 8px;
  font-family: 'SatoshiBold', sans-serif;
  font-size: 14px;
  cursor: pointer;

  &:disabled { opacity: 0.6; cursor: default; }
`;

// Bundled SVG ships white; brightness(0) repaints fills black so the
// triangle reads against the lime CTA background.
const PlayIcon = styled.img`
  width: 12px;
  height: 12px;
  filter: brightness(0);
`;

const DnpBox = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border: 1px dashed ${(props) => props.theme.borderColor};
  border-radius: 12px;
`;

const DnpBadge = styled.span`
  background: ${(props) => props.theme.borderColor};
  color: ${(props) => props.theme.white};
  padding: 4px 10px;
  border-radius: 4px;
  font-family: 'SatoshiBold', sans-serif;
  font-size: 12px;
  letter-spacing: 0.5px;
`;

const DnpText = styled.span`
  font-size: 14px;
  color: ${(props) => props.theme.blueish2};
`;

const StatsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const HeadlineStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
`;

const HeadlineCell = styled.div`
  background: ${(props) => props.theme.row};
  border-radius: 12px;
  padding: 14px 10px;
  text-align: center;
`;

const HeadlineValue = styled.div`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 22px;
`;

const HeadlineLabel = styled.div`
  font-size: 11px;
  color: ${(props) => props.theme.blueish2};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-top: 4px;
`;

// Tiny inline checkbox label that controls whether zero-value stats are
// hidden. Subtle by design — it's a configuration affordance, not a
// primary action.
const ZeroToggle = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  align-self: flex-start;
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 12px;
  color: ${(props) => props.theme.blueish2};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  padding: 0 4px;

  & > span { color: inherit; }
`;

const ZeroToggleInput = styled.input`
  width: 16px;
  height: 16px;
  accent-color: ${(props) => props.theme.green};
  cursor: pointer;
`;

const StatGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const GroupTitle = styled.h3`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${(props) => props.theme.blueish2};
  margin: 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
`;

const StatCard = styled.div`
  position: relative;
  background: ${(props) => props.theme.row};
  border-radius: 10px;
  padding: 12px;
  /* Reset the <button> defaults when rendered as="button" so the clickable
     variant looks identical to the static card. */
  border: 0;
  margin: 0;
  width: 100%;
  text-align: left;
  color: inherit;
  font: inherit;

  ${(props) => props.$clickable && `
    cursor: pointer;
    transition: background 120ms ease, transform 120ms ease;

    &:hover { background: rgba(255, 255, 255, 0.06); }
    &:active { transform: scale(0.98); }
    &:disabled { opacity: 0.5; cursor: default; transform: none; }
  `}
`;

// Small play glyph in the corner of a tappable stat card — signals that the
// number opens video (only some cards are watchable, so the hint disambiguates).
const StatPlayHint = styled.img`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 12px;
  height: 12px;
  opacity: 0.5;
`;

const StatValue = styled.div`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 20px;
`;

const StatLabel = styled.div`
  font-size: 11px;
  color: ${(props) => props.theme.blueish2};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-top: 2px;
`;

const EventsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const EventsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const EventsTitle = styled.h3`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${(props) => props.theme.blueish2};
  margin: 0;
`;

const SelectLinks = styled.div`
  display: flex;
  gap: 12px;
`;

const CategoryStrip = styled.div`
  display: flex;
  gap: 6px;
  overflow-x: auto;
  padding-bottom: 4px;
  margin: -2px -16px 4px;
  padding-left: 16px;
  padding-right: 16px;
  scroll-snap-type: x proximity;

  /* hide scrollbar — chip strip is touch-scroll only */
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const CategoryChip = styled.button`
  flex: 0 0 auto;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid ${(props) => (props.$selected ? props.theme.green : props.theme.borderColor)};
  background: ${(props) => (props.$selected ? props.theme.green : 'transparent')};
  color: ${(props) => (props.$selected ? props.theme.black : props.theme.white)};
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 12px;
  white-space: nowrap;
  cursor: pointer;
  scroll-snap-align: start;
`;

const SelectLink = styled.button`
  background: transparent;
  border: 0;
  padding: 0;
  color: ${(props) => props.theme.green};
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 12px;
  cursor: pointer;
`;

// Sub-section header for the parameter sub-picker (e.g. "GOALS/CHANCES/SHOTS")
// shown when a category chip is active. Mirrors the Events page rhythm.
const SubSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 4px 0;
`;

const SectionLabel = styled.h3`
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  color: ${(props) => props.theme.blueish2};
  margin: 0;
`;

const SectionActions = styled.div`
  display: flex;
  gap: 14px;
`;

const ActionLink = styled.button`
  background: transparent;
  border: 0;
  padding: 0;
  color: ${(props) => props.theme.green};
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  cursor: pointer;

  &:hover { opacity: 0.8; }
`;

const ParamCard = styled.div`
  display: flex;
  flex-direction: column;
  background: ${(props) => props.theme.row};
  border: 1px solid ${(props) => props.theme.borderColor};
  border-radius: 12px;
  overflow: hidden;
`;

// Slim neutral scrollbar inside the card so it doesn't compete with content.
const ParamScroll = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 240px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: ${(props) => props.theme.borderColor} transparent;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background-color: ${(props) => props.theme.borderColor};
    border-radius: 999px;
  }
`;

const ParamRow = styled.label`
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 12px 14px;
  cursor: pointer;
  border-top: 1px solid ${(props) => props.theme.borderColor};

  ${(props) => props.$first && `border-top: 0;`}

  &:hover { background: rgba(255, 255, 255, 0.02); }
`;

const ParamCheckbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: ${(props) => props.theme.green};
  flex: 0 0 auto;
  cursor: pointer;
`;

const ParamMain = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex: 1;
  min-width: 0;
`;

const ParamName = styled.span`
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 14px;
  color: ${(props) => props.theme.white};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const ParamCount = styled.span`
  flex-shrink: 0;
  font-family: 'SatoshiBold', sans-serif;
  font-size: 11px;
  letter-spacing: 0.4px;
  color: ${(props) => props.theme.blueish2};
  background: rgba(255, 255, 255, 0.04);
  padding: 2px 8px;
  border-radius: 999px;
`;

// Body-part disclosure — collapsed by default; tap to reveal chips.
const Disclosure = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 4px;
`;

const DisclosureToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  background: ${(props) => props.theme.row};
  border: 1px solid ${(props) => props.theme.borderColor};
  border-radius: 12px;
  color: ${(props) => props.theme.white};
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  transition: border-color 120ms ease;

  &:hover { border-color: ${(props) => props.theme.blueish2}; }
`;

const DisclosureLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  font-size: 11px;
  color: ${(props) => props.theme.blueish2};
`;

const DisclosureValue = styled.span`
  color: ${(props) => props.theme.white};
  text-transform: none;
  letter-spacing: 0.2px;
  font-size: 13px;
`;

const DisclosureChevron = styled.span`
  font-size: 18px;
  line-height: 1;
  color: ${(props) => props.theme.blueish2};
  transform: rotate(${(props) => (props.$open ? '90deg' : '0')});
  transition: transform 160ms ease;
`;

const BodyPartChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 4px;
`;

const EventsList = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${(props) => props.theme.borderColor};
  border-radius: 10px;
  overflow: hidden;
`;

const EventRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: ${(props) => (props.$checked ? props.theme.row : 'transparent')};
  border-top: 1px solid ${(props) => props.theme.borderColor};

  &:first-child { border-top: 0; }
`;

const EventCheckbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: ${(props) => props.theme.green};
  cursor: pointer;
  flex-shrink: 0;
`;

const EventMain = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex: 1;
  padding: 2px 0;
  background: transparent;
  border: 0;
  color: ${(props) => props.theme.white};
  cursor: pointer;
  text-align: left;

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

const EventName = styled.span`
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 14px;
`;

const EventTime = styled.span`
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 12px;
  color: ${(props) => props.theme.blueish2};
  flex-shrink: 0;
`;

// Mirrors the floating Watch bar from Events.jsx — fixed above the bottom
// nav so the CTA stays in reach while the user scrolls long event lists.
const WatchBar = styled.div`
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  bottom: calc(96px + env(safe-area-inset-bottom, 0px));
  display: flex;
  justify-content: center;
  width: 100%;
  max-width: 480px;
  padding: 0 16px;
  box-sizing: border-box;
  pointer-events: none;
  z-index: 11;
`;

const WatchButton = styled.button`
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 22px;
  border: 0;
  border-radius: 999px;
  background: ${(props) => props.theme.green};
  color: ${(props) => props.theme.black};
  font-family: 'SatoshiBold', sans-serif;
  font-size: 14px;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.4);

  &:disabled { opacity: 0.6; cursor: default; }
`;
